// Client for the archive server — the service that owns the canonical
// per-account singleton archive and serializes all merges
// (archive_design.md, Version 4 amendment; endpoints under /archive/v1).
//
// This module is transport only: submissions, manifest/file reads, adopt, and
// the session channel. The local-file side (handle, durable write) stays in
// archiveApi.js — the console holds the only file handle, by design.

import { getServiceUrl } from '../sysconfig';

let _baseOverride = null;

/** Test hook / manual override. */
export function setArchiveServerBase(url) {
    _baseOverride = url || null;
}

async function getBase() {
    if (_baseOverride) return _baseOverride.replace(/\/+$/, '');
    const envBase = process.env.REACT_APP_ARCHIVE_SERVER_URL;
    if (envBase) return envBase.replace(/\/+$/, '');
    const url = await getServiceUrl('archive_server');
    if (!url) throw new Error('Archive server URL not found in sysconfig (key "archive_server")');
    return url.replace(/\/+$/, '');
}

function getOrgHeaders() {
    const headers = {};
    try {
        // Lazy require to avoid an import cycle with cloudApi.
        const { getOrgContext } = require('./cloudApi');
        const ctx = getOrgContext();
        if (ctx.orgId) headers['X-Org-ID'] = ctx.orgId;
        if (ctx.userId) headers['X-User-ID'] = ctx.userId;
    } catch (_) { /* swallow */ }
    return headers;
}

async function parseError(response, fallback) {
    let text = '';
    try { text = await response.text(); } catch (_) { }
    let parsed = {};
    try { parsed = JSON.parse(text); } catch (_) { }
    const message = parsed?.error?.message || parsed?.message || text || response.statusText || fallback;
    const err = new Error(message);
    err.status = response.status;
    err.code = parsed?.error?.code || '';
    err.data = parsed;
    return err;
}

function unwrap(json) {
    return json && json.data !== undefined ? json.data : json;
}

/**
 * Submit a contextual archive selection. Blocks until the merge lands on the
 * canonical singleton (the server serializes per account).
 * @param {object} selector { survey_ids? | recipient_ids? | deployment_ids? }
 * @returns {Promise<{manifest_checksum: string}>} plus artifact counts
 */
export async function submitSelective(selector) {
    const base = await getBase();
    const response = await fetch(`${base}/archive/v1/selective`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getOrgHeaders() },
        body: JSON.stringify({ selector }),
    });
    if (!response.ok) throw await parseError(response, 'Archive submission failed');
    return unwrap(await response.json());
}

/**
 * Read the canonical singleton's derived manifest.
 * Returns null when no archive exists yet (404 NO_ARCHIVE).
 */
export async function fetchServerManifest() {
    const base = await getBase();
    const response = await fetch(`${base}/archive/v1/manifest`, { headers: getOrgHeaders() });
    if (response.status === 404) return null;
    if (!response.ok) throw await parseError(response, 'Failed to read archive manifest');
    return unwrap(await response.json()); // { manifest, manifest_checksum }
}

/**
 * Get a signed download URL for the current canonical file.
 * Returns null when no archive exists yet.
 */
export async function fetchFileRef() {
    const base = await getBase();
    const response = await fetch(`${base}/archive/v1/file`, { headers: getOrgHeaders() });
    if (response.status === 404) return null;
    if (!response.ok) throw await parseError(response, 'Failed to get archive download reference');
    return unwrap(await response.json()); // { download_ref, manifest_checksum }
}

/**
 * Adopt local bytes as the canonical singleton (resync). The local file — the
 * copy the user actually holds — is authoritative when they say so.
 */
export async function adoptLocalArchive(bytes, filename = 'workspace.samarchive') {
    const base = await getBase();
    const formData = new FormData();
    formData.append('archive', new Blob([bytes || new Uint8Array()], { type: 'application/zip' }), filename);
    const response = await fetch(`${base}/archive/v1/adopt`, {
        method: 'POST',
        headers: getOrgHeaders(),
        body: formData,
    });
    if (!response.ok) throw await parseError(response, 'Adopt failed');
    return unwrap(await response.json()); // { manifest_checksum }
}

/**
 * Open the session channel (SSE). Implemented over fetch rather than
 * EventSource because EventSource cannot carry the X-Org-ID/X-User-ID auth
 * headers. Reconnects with backoff until closed; every (re)connect delivers
 * the server's "hello" event, so a connection gap self-heals by checksum
 * comparison — no event replay needed.
 *
 * @param {object} handlers
 * @param {(ev: object) => void} handlers.onEvent   parsed event objects
 *        ({type, manifest_checksum, ...}; types: hello | merge | adopt)
 * @param {(state: 'connected'|'reconnecting'|'closed') => void} [handlers.onStatus]
 * @returns {{ close: () => void }}
 */
export function openArchiveChannel({ onEvent, onStatus }) {
    let closed = false;
    let controller = null;
    let backoffMs = 1000;

    const setStatus = (s) => { try { onStatus && onStatus(s); } catch (_) { } };

    const parseSSE = (buffer, emit) => {
        // Standard SSE framing: blocks separated by a blank line; we read the
        // data: lines (event name is repeated inside the JSON as .type).
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const block = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const dataLines = block
                .split('\n')
                .filter(l => l.startsWith('data:'))
                .map(l => l.slice(5).trim());
            if (dataLines.length === 0) continue; // heartbeat comment
            try { emit(JSON.parse(dataLines.join('\n'))); } catch (_) { }
        }
        return buffer;
    };

    const run = async () => {
        while (!closed) {
            controller = new AbortController();
            try {
                const base = await getBase();
                const response = await fetch(`${base}/archive/v1/channel`, {
                    headers: { Accept: 'text/event-stream', ...getOrgHeaders() },
                    signal: controller.signal,
                });
                if (!response.ok || !response.body) throw await parseError(response, 'Channel connect failed');

                setStatus('connected');
                backoffMs = 1000;

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    buffer = parseSSE(buffer, (ev) => { try { onEvent && onEvent(ev); } catch (_) { } });
                }
            } catch (_) {
                // fall through to reconnect
            }
            if (closed) break;
            setStatus('reconnecting');
            await new Promise(res => setTimeout(res, backoffMs));
            backoffMs = Math.min(backoffMs * 2, 30000);
        }
        setStatus('closed');
    };

    run();

    return {
        close() {
            closed = true;
            try { controller && controller.abort(); } catch (_) { }
        },
    };
}
